import { useAnalytics } from '@repo/analytics/analytics-provider';
import { toast } from '@repo/design-system/components/ui/toaster';
import { useHostType } from '@repo/host/hooks/use-host-info';
import { useT } from '@repo/i18n/components/i18n.client';
import { useBulkUpdatePinStatusesMutation } from '@repo/store/maps/api/mapsApi';
import { useAppDispatch, useAppSelector } from '@repo/store/maps/hooks';
import { selectAuthState } from '@repo/store/maps/slices/auth';
import { selectExternalMapId, selectFilterState, selectHiddenRegions, selectLayerVisibility, selectMapData, showOnlyRegionPins, toggleRegionPinVisibility } from '@repo/store/maps/slices/mapData';
import { isPointInRegion } from '@repo/types/maps/map-utils';
import type * as MapLibre from 'maplibre-gl';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMapContext } from '../map-context';
import { PIN_INTERACTION_LAYER_IDS } from './constants';
export interface RegionContextMenuState {
  isOpen: boolean;
  x: number;
  y: number;
  regionId: string;
  regionTitle: string;
  isRegionHidden: boolean;
}
const INITIAL_STATE: RegionContextMenuState = {
  isOpen: false,
  x: 0,
  y: 0,
  regionId: '',
  regionTitle: '',
  isRegionHidden: false
};
export function useRegionContextMenu() {
  const {
    map
  } = useMapContext();
  const dispatch = useAppDispatch();
  const mapData = useAppSelector(selectMapData);
  const hiddenRegions = useAppSelector(selectHiddenRegions);
  const layerVisibility = useAppSelector(selectLayerVisibility);
  const externalMapId = useAppSelector(selectExternalMapId);
  const auth = useAppSelector(selectAuthState);
  const hostType = useHostType();
  const analytics = useAnalytics();
  const {
    t
  } = useT(['game_map']);
  const [bulkUpdatePinStatuses] = useBulkUpdatePinStatusesMutation();
  const [menuState, setMenuState] = useState<RegionContextMenuState>(INITIAL_STATE);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const filters = useAppSelector(selectFilterState);
  const hiddenRegionsRef = useRef(hiddenRegions);
  const mapDataRef = useRef(mapData);
  const regionsVisibleRef = useRef(layerVisibility.regions);
  const filtersRef = useRef(filters);
  useEffect(() => {
    hiddenRegionsRef.current = hiddenRegions;
  }, [hiddenRegions]);
  useEffect(() => {
    mapDataRef.current = mapData;
  }, [mapData]);
  useEffect(() => {
    regionsVisibleRef.current = layerVisibility.regions;
  }, [layerVisibility.regions]);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);
  const close = useCallback(() => {
    setMenuState(INITIAL_STATE);
  }, []);
  const toggleRegionPins = useCallback(() => {
    if (menuState.regionId) {
      dispatch(toggleRegionPinVisibility(menuState.regionId));
    }
    close();
  }, [menuState.regionId, dispatch, close]);
  const showOnlyRegion = useCallback(() => {
    if (menuState.regionId) {
      dispatch(showOnlyRegionPins(menuState.regionId));
    }
    close();
  }, [menuState.regionId, dispatch, close]);
  useEffect(() => {
    if (!map) return;
    const handleContextMenu = (e: MapLibre.MapMouseEvent) => {
      if (!regionsVisibleRef.current) return;
      const regions = mapDataRef.current?.regions?.features;
      if (!regions?.length) return;
      const markerLayers = PIN_INTERACTION_LAYER_IDS.filter(id => !!map.getLayer(id));
      if (markerLayers.length > 0) {
        let markerFeatures: MapLibre.MapGeoJSONFeature[] = [];
        try {
          markerFeatures = map.queryRenderedFeatures(e.point, {
            layers: markerLayers
          });
        } catch {
          // maplibre dictionary_coder crashes when a tile's symbol index is empty
        }
        if (markerFeatures.length > 0) return;
      }
      const lngLat: [number, number] = [e.lngLat.lng, e.lngLat.lat];
      for (const region of regions) {
        if (isPointInRegion(lngLat, region.geometry)) {
          e.originalEvent.preventDefault();
          e.originalEvent.stopPropagation();
          const regionId = region.properties.externalId;
          const regionTitle = region.properties.title || regionId;
          const isHidden = hiddenRegionsRef.current.includes(regionId);
          setMenuState({
            isOpen: true,
            x: e.originalEvent.clientX,
            y: e.originalEvent.clientY,
            regionId,
            regionTitle,
            isRegionHidden: isHidden
          });
          return;
        }
      }
    };
    map.on('contextmenu', handleContextMenu);
    return () => {
      map.off('contextmenu', handleContextMenu);
    };
  }, [map]);
  const bulkUpdateRegionPins = useCallback(async (found: boolean) => {
    if (!externalMapId || !menuState.regionId || !mapDataRef.current) {
      close();
      return;
    }
    if (!auth.isAuthenticated) {
      close();
      setIsLoginModalOpen(true);
      return;
    }
    const region_0 = mapDataRef.current.regions?.features.find(r => r.properties.externalId === menuState.regionId);
    if (!region_0) {
      close();
      return;
    }
    const hiddenSubcategories = new Set(filtersRef.current.hiddenSubcategories);
    const pins = mapDataRef.current.geojson.features.filter(f => {
      if (f.properties.hidden) return false;
      if (hiddenSubcategories.has(f.properties.subcategoryExternalId)) return false;
      const coords: [number, number] = [f.geometry.coordinates[0], f.geometry.coordinates[1]];
      return isPointInRegion(coords, region_0.geometry);
    }).map(f_0 => ({
      pinnableType: (f_0.properties.isCustomPin ? 'user_pin' : 'pin') as 'pin' | 'user_pin',
      pinnableId: f_0.properties.externalId
    }));
    if (pins.length === 0) {
      close();
      return;
    }
    close();
    try {
      await bulkUpdatePinStatuses({
        externalId: externalMapId,
        pins,
        found
      }).unwrap();
      if (analytics && hostType) {
        analytics.event('game_map_bulk_update_pin_status', {
          mapId: externalMapId,
          found,
          pinCount: pins.length,
          trigger: 'region-context-menu',
          platform: hostType
        });
      }
    } catch {
      toast({
        title: t($ => $.error),
        description: found ? t($_0 => $_0.mark_all_found_error) : t($_1 => $_1.mark_all_unfound_error),
        variant: 'error'
      });
    }
  }, [externalMapId, auth.isAuthenticated, menuState.regionId, bulkUpdatePinStatuses, close, analytics, hostType, t]);
  return {
    menuState,
    close,
    toggleRegionPins,
    showOnlyRegion,
    bulkUpdateRegionPins,
    isLoginModalOpen,
    setIsLoginModalOpen
  };
}